import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/products/${id}`);
        setProduct(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load product details');
        setLoading(false);
        console.error('Error fetching product details:', err);
      }
    };

    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="alert alert-danger" role="alert">
        {error || 'Product not found'}
      </div>
    );
  }

  // Improved image source logic
  const getImageSrc = () => {
    // First try imageUrl (single image)
    if (product.imageUrl) {
      return product.imageUrl;
    }
    
    // Then try images array
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    
    // Fallback to placeholder
    return 'https://placehold.co/400x300/e9ecef/495057?text=No+Image+Available';
  };

  const handleImageError = (e) => {
    console.log('Image failed to load, using fallback');
    e.target.src = 'https://placehold.co/400x300/f8d7da/721c24?text=Image+Not+Found';
    setImageError(true);
  };

  const imageSrc = getImageSrc();
  const stockClass = product.stock > 10 ? 'high-stock' : (product.stock > 3 ? 'medium-stock' : 'low-stock');
  const unitMedia = Array.isArray(product.imeiTracking)
    ? product.imeiTracking
        .map((row, idx) => ({
          key: `${row.imei1 || 'unit'}-${idx}`,
          imei1: row.imei1 || '',
          imei2: row.imei2 || '',
          status: row.status || '',
          grade: row.unitConditionGrade || '',
          photos: Array.isArray(row.unitRealPhotos) ? row.unitRealPhotos.filter(Boolean) : [],
          url: typeof row.unitRealVideoUrl === 'string' ? row.unitRealVideoUrl.trim() : '',
          videoStatus: row.unitRealVideoStatus || '',
          videoError: row.unitRealVideoError || ''
        }))
        .filter(
          (row) =>
            String(row.status || 'available') === 'available' &&
            (row.photos.length > 0 || row.url || row.videoStatus === 'processing' || row.videoStatus === 'failed')
        )
    : [];

  // Add ordinal suffix helper function (kept in case other parts of the page might use it, can be removed if not)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Get current price based on selected variant
  const getCurrentPrice = () => {
    if (selectedVariant && selectedVariant.price) {
      return selectedVariant.price;
    }
    return product.price;
  };

  // Get current buying price based on selected variant
  const getCurrentBuyingPrice = () => {
    if (selectedVariant && selectedVariant.buyingPrice) {
      return selectedVariant.buyingPrice;
    }
    return product.buyingPrice;
  };

  // Handle variant selection
  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
  };

  return (
    <div>
      <button 
        className="btn btn-outline-secondary mb-4"
        onClick={() => navigate('/')}
      >
        &larr; Back to Products
      </button>
      
      <div className="card">
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 text-center">
              <div className="position-relative">
                <img 
                  src={imageSrc} 
                  className="img-fluid product-image" 
                  alt={product.name} 
                  style={{ 
                    maxHeight: '300px', 
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                  onError={handleImageError}
                />
                {imageError && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                    <div className="text-center text-muted">
                      <i className="bi bi-image" style={{ fontSize: '3rem' }}></i>
                      <br />
                      <small>Image not available</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="col-md-8">
              <h2>{product.name}</h2>
              <p className="text-muted">Category: {product.category}</p>
              {product.brand && (
                <p className="text-muted">Brand: {product.brand}</p>
              )}
              
              {product.description && (
                <p>{product.description}</p>
              )}
              
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="text-primary mb-0">₹{getCurrentPrice().toLocaleString()}</h4>
                <span className={`badge ${product.stock > 0 ? 'bg-success' : 'bg-danger'}`}>
                  {product.stock > 0 ? `In Stock (${product.stock})` : 'Out of Stock'}
                </span>
              </div>

              {selectedVariant && selectedVariant.offerPrice && (
                <div className="mb-3">
                  <h5 className="text-success">
                    Offer Price: ₹{selectedVariant.offerPrice.toLocaleString()}
                  </h5>
                </div>
              )}

              {!selectedVariant && product.offerPrice && (
                <div className="mb-3">
                  <h5 className="text-success">
                    Offer Price: ₹{product.offerPrice.toLocaleString()}
                  </h5>
                </div>
              )}

              {/* Specifications */}
              {product.specs && (
                <div className="mb-4">
                  <h5>Specifications</h5>
                  <div className="row g-2">
                    {product.specs.ramGB ? (
                      <div className="col-sm-6"><strong>RAM:</strong> {product.specs.ramGB} GB</div>
                    ) : null}
                    {product.specs.storageGB ? (
                      <div className="col-sm-6"><strong>Storage:</strong> {product.specs.storageGB} GB</div>
                    ) : null}
                    {product.specs.processor ? (
                      <div className="col-sm-6"><strong>Processor:</strong> {product.specs.processor}</div>
                    ) : null}
                    {product.specs.displayInches ? (
                      <div className="col-sm-6"><strong>Display:</strong> {product.specs.displayInches}"</div>
                    ) : null}
                    {product.specs.network ? (
                      <div className="col-sm-6"><strong>Network:</strong> {product.specs.network}</div>
                    ) : null}
                    {product.specs.os ? (
                      <div className="col-sm-6"><strong>Operating System:</strong> {product.specs.os}</div>
                    ) : null}
                    {product.specs.rearCameraMP ? (
                      <div className="col-sm-6"><strong>Rear Camera:</strong> {product.specs.rearCameraMP} MP</div>
                    ) : null}
                    {product.specs.frontCameraMP ? (
                      <div className="col-sm-6"><strong>Front Camera:</strong> {product.specs.frontCameraMP} MP</div>
                    ) : null}
                    {product.specs.batteryMah ? (
                      <div className="col-sm-6"><strong>Battery:</strong> {product.specs.batteryMah} mAh</div>
                    ) : null}
                    {product.specs.color ? (
                      <div className="col-sm-6"><strong>Color:</strong> {product.specs.color}</div>
                    ) : null}
                    {product.specs.screenType ? (
                      <div className="col-sm-6"><strong>Screen Type:</strong> {product.specs.screenType}</div>
                    ) : null}
                    {product.specs.resolution ? (
                      <div className="col-sm-6"><strong>Resolution:</strong> {product.specs.resolution}</div>
                    ) : null}
                    {typeof product.specs.smartTV === 'boolean' && product.category === 'TV' ? (
                      <div className="col-sm-6"><strong>Smart TV:</strong> {product.specs.smartTV ? 'Yes' : 'No'}</div>
                    ) : null}
                    {product.specs.powerWatts ? (
                      <div className="col-sm-6"><strong>Power:</strong> {product.specs.powerWatts} W</div>
                    ) : null}
                    {product.specs.capacity ? (
                      <div className="col-sm-6"><strong>Capacity:</strong> {product.specs.capacity}</div>
                    ) : null}
                    {product.specs.energyRating ? (
                      <div className="col-sm-6"><strong>Energy Rating:</strong> {product.specs.energyRating} Star</div>
                    ) : null}
                  </div>
                </div>
              )}

              {unitMedia.length > 0 && (
                <div className="mb-4">
                  <h5>Per-IMEI Real Photos / Video</h5>
                  {unitMedia.some((clip) => clip.videoStatus === 'processing') ? (
                    <div className="alert alert-info py-2 small">
                      Video upload ho gayi hai. Server compress kar raha hai; 1-3 minute baad refresh karne par clip dikhegi.
                    </div>
                  ) : null}
                  <div className="row g-3">
                    {unitMedia.map((clip, idx) => (
                      <div key={clip.key} className="col-md-6">
                        <div className="border rounded p-2 bg-light">
                          <div className="small text-muted mb-2">
                            Unit {idx + 1}
                            {clip.imei1 ? (
                              <>
                                {' '}· IMEI <code>{clip.imei1}</code>
                              </>
                            ) : null}
                            {clip.imei2 ? (
                              <>
                                {' '}· <code>{clip.imei2}</code>
                              </>
                            ) : null}
                            {clip.grade ? (
                              <>
                                {' '}· Grade <strong>{clip.grade}</strong>
                              </>
                            ) : null}
                          </div>
                          {clip.photos.length > 0 ? (
                            <div className="d-flex gap-2 flex-wrap mb-2">
                              {clip.photos.map((src) => (
                                <a key={src} href={src} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={src}
                                    alt=""
                                    style={{
                                      width: 90,
                                      height: 90,
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                      border: '1px solid #dee2e6'
                                    }}
                                  />
                                </a>
                              ))}
                            </div>
                          ) : null}
                          {clip.url ? (
                            <video
                              src={clip.url}
                              controls
                              muted
                              playsInline
                              preload="metadata"
                              style={{
                                width: '100%',
                                maxHeight: 260,
                                borderRadius: 8,
                                backgroundColor: '#000'
                              }}
                            />
                          ) : clip.videoStatus === 'failed' ? (
                            <div className="alert alert-danger py-2 small mb-0">
                              Video process nahi ho payi: {clip.videoError || 'Please upload again'}
                            </div>
                          ) : (
                            <div className="alert alert-info py-2 small mb-0">
                              Video processing… thodi der baad refresh karein.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.variants && product.variants.length > 0 && (
                <div className="mb-4">
                  <h5>Select Variant:</h5>
                  <div className="row g-2">
                    {product.variants.map((variant, index) => (
                      <div key={index} className="col-md-6">
                        <div 
                          className={`card cursor-pointer ${selectedVariant === variant ? 'border-primary bg-light' : 'border-secondary'}`}
                          onClick={() => handleVariantSelect(variant)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="mb-1">{variant.name}: {variant.value}</h6>
                                <small className="text-muted">
                                  Buying: ₹{variant.buyingPrice?.toLocaleString() || product.buyingPrice.toLocaleString()}
                                </small>
                              </div>
                              <div className="text-end">
                                <strong className="text-primary">₹{variant.price?.toLocaleString() || product.price.toLocaleString()}</strong>
                                {variant.offerPrice && (
                                  <div className="text-success small">₹{variant.offerPrice.toLocaleString()}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedVariant && (
                    <div className="mt-3 p-3 bg-primary text-white rounded">
                      <h6 className="mb-1">Selected: {selectedVariant.name} - {selectedVariant.value}</h6>
                      <div className="d-flex justify-content-between">
                        <span>Price: ₹{selectedVariant.price?.toLocaleString() || product.price.toLocaleString()}</span>
                        <span>Buying: ₹{selectedVariant.buyingPrice?.toLocaleString() || product.buyingPrice.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(product.tags) && product.tags.length > 0 && (
                <div className="mb-3">
                  <h6>Tags</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {product.tags.map((tag, idx) => (
                      <span key={idx} className="badge bg-secondary">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(product.features) && product.features.length > 0 && (
                <div className="mb-3">
                  <h6>Key Features</h6>
                  <ul className="mb-0">
                    {product.features.map((f, idx) => (
                      <li key={idx}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* EMI related button and form removed */}

            </div>
          </div>
        </div>
        
        <div className="card-footer">
          <div className="d-flex justify-content-end">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/inventory')}
            >
              <i className="bi bi-box-seam me-1"></i>
              Go to Inventory
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail; 